import React, { useCallback, useEffect, useRef } from 'react';

import { taskDisplayTitle } from '../utils/taskDisplayLabels';

import './ActivityRing.css';
import './TaskChecklist.css';

export interface TaskChecklistItem {
  id: string;
  title: string;
  status: string;
  statusLabel: string;
  statusTitle?: string;
  detail?: React.ReactNode;
  selectable?: boolean;
  checked?: boolean;
  indeterminate?: boolean;
  selectionDependency?: boolean;
  completionOnly?: boolean;
  viewable?: boolean;
  activityFrozen?: boolean;
}

interface TaskChecklistProps {
  ariaLabel: string;
  heading: string;
  headingAction?: React.ReactNode;
  totals: React.ReactNode;
  items: TaskChecklistItem[];
  completionOnly?: boolean;
  onSelectionChange?: (taskId: string, checked: boolean) => void;
  onViewTask?: (taskId: string) => void;
}

const displayTitle = (item: TaskChecklistItem): string => taskDisplayTitle(item.title);

const isTaskBusy = (item: TaskChecklistItem): boolean => !item.activityFrozen
  && ['queued', 'ai_in_progress', 'human_in_progress'].includes(item.status);

const TaskChecklistTitle: React.FC<{ item: TaskChecklistItem }> = ({ item }) => {
  const title = displayTitle(item);
  const textRef = useRef<HTMLElement>(null);
  const measureTitle = useCallback(() => {
    const text = textRef.current;
    const label = text?.parentElement;

    if (!text || !label) return;
    if (text.scrollWidth > text.clientWidth) label.title = item.title;
    else label.removeAttribute('title');
  }, [item.title]);

  useEffect(() => {
    measureTitle();
    const observer = typeof globalThis.ResizeObserver === 'function'
      ? new globalThis.ResizeObserver(measureTitle) : null;

    if (textRef.current) observer?.observe(textRef.current);

    return () => observer?.disconnect();
  }, [title, measureTitle]);

  return (
    <span className="phase-task-picker__task-title" tabIndex={0}
      onMouseEnter={measureTitle} onFocus={measureTitle}>
      <span ref={textRef} className="phase-task-picker__task-title-text">{title}</span>
    </span>
  );
};

const TaskChecklistContent: React.FC<{ item: TaskChecklistItem }> = ({ item }) => (
  <>
    <TaskChecklistTitle item={item} />
    <span className={`phase-task-picker__task-status phase-task-picker__task-status--${item.status}`}
      title={item.statusTitle || item.statusLabel}>
      {item.statusLabel}
    </span>
    {item.detail !== undefined && item.detail !== null && (
      <span className="phase-task-picker__task-credit">{item.detail}</span>
    )}
  </>
);

const TaskCompletionMark: React.FC<{ item: TaskChecklistItem }> = ({ item }) => {
  const busy = isTaskBusy(item);
  const statusDescription = busy && item.status !== 'queued'
    ? 'running' : item.status === 'done' ? 'completed' : item.statusLabel;

  return (
    <span className={`phase-task-picker__check phase-task-picker__check--readonly${item.status === 'done' ? ' phase-task-picker__check--done' : ''}${busy ? ' phase-task-picker__check--busy activity-ring' : ''}`}
      role="checkbox" aria-readonly="true" aria-checked={item.status === 'done'} aria-busy={busy || undefined}
      aria-label={`${displayTitle(item)} ${statusDescription}`}>
      <span aria-hidden="true">{item.status === 'done' ? '✓' : item.status === 'error' ? '!' : ''}</span>
    </span>
  );
};

const TaskSelectionDependencyMark: React.FC<{ item: TaskChecklistItem }> = ({ item }) => (
  <span className="phase-task-picker__dependency" role="img"
    aria-label={`${displayTitle(item)} is included with its selected stage`}>
    <i className="fa-solid fa-link" aria-hidden="true" />
  </span>
);

const TaskChecklistSelection: React.FC<{
  item: TaskChecklistItem;
  completionOnly: boolean;
  onSelectionChange?: TaskChecklistProps['onSelectionChange'];
}> = ({ item, completionOnly, onSelectionChange }) => {
  if (completionOnly || isTaskBusy(item)) return (
    <div className="phase-task-picker__task-select phase-task-picker__task-select--disabled">
      <TaskCompletionMark item={item} />
      <TaskChecklistContent item={item} />
    </div>
  );
  if (item.status === 'done') return (
    <div className="phase-task-picker__task-select phase-task-picker__task-select--disabled phase-task-picker__task-select--completed">
      <TaskChecklistContent item={item} />
    </div>
  );
  if (item.selectionDependency) return (
    <label className={`phase-task-picker__task-select phase-task-picker__task-select--dependency${item.selectable ? '' : ' phase-task-picker__task-select--disabled'}`}>
      <input checked={Boolean(item.checked)} disabled={!item.selectable} type="checkbox"
        aria-label={displayTitle(item)}
        onChange={(event) => onSelectionChange?.(item.id, event.target.checked)} />
      <TaskSelectionDependencyMark item={item} />
      <TaskChecklistContent item={item} />
    </label>
  );

  return (
    <label className={`phase-task-picker__task-select${item.selectable ? '' : ' phase-task-picker__task-select--disabled'}`}>
      <input checked={Boolean(item.checked)} disabled={!item.selectable} type="checkbox"
        aria-label={displayTitle(item)} aria-checked={item.indeterminate ? 'mixed' : Boolean(item.checked)}
        ref={(input) => { if (input) input.indeterminate = Boolean(item.indeterminate); }}
        onChange={(event) => onSelectionChange?.(item.id, event.target.checked)} />
      <span className="phase-task-picker__check" aria-hidden="true">
        <i className={`fa-solid ${item.indeterminate ? 'fa-minus' : 'fa-check'}`} />
      </span>
      <TaskChecklistContent item={item} />
    </label>
  );
};

interface TaskChecklistRowsProps {
  items: TaskChecklistItem[];
  completionOnly?: boolean;
  onSelectionChange?: TaskChecklistProps['onSelectionChange'];
  onViewTask?: TaskChecklistProps['onViewTask'];
}

/** The rows alone, for a caller that owns its own heading (the running-tasks dock). */
export const TaskChecklistRows: React.FC<TaskChecklistRowsProps> = ({
  items, completionOnly = false, onSelectionChange, onViewTask,
}) => (
  <div className="phase-task-picker__list">
    {items.map((item) => (
      <div key={item.id}
        className={`phase-task-picker__task${item.checked && item.status !== 'error' && !completionOnly && !item.selectionDependency ? ' phase-task-picker__task--selected' : ''}${item.checked && item.status === 'error' && !completionOnly && !item.selectionDependency ? ' phase-task-picker__task--retry-selected' : ''}${item.selectionDependency && !completionOnly ? ' phase-task-picker__task--dependency' : ''}${!item.selectable && !completionOnly && !item.completionOnly && !item.selectionDependency ? ' phase-task-picker__task--unavailable' : ''}${item.status === 'done' ? ' phase-task-picker__task--completed' : ''}`}>
        <TaskChecklistSelection item={item} completionOnly={completionOnly || Boolean(item.completionOnly)}
          onSelectionChange={onSelectionChange} />
        {onViewTask && item.viewable !== false && (
          <button aria-label={`View task: ${displayTitle(item)}`} className="phase-task-picker__view" type="button"
            onClick={() => onViewTask(item.id)}>
            <i className="fa-solid fa-eye" aria-hidden="true" />
            <span className="phase-task-picker__view-label">View</span>
          </button>
        )}
      </div>
    ))}
  </div>
);

/** Shared original task picker presentation; its caller owns selection or execution state. */
export const TaskChecklist: React.FC<TaskChecklistProps> = ({
  ariaLabel, heading, headingAction, totals, items, completionOnly = false, onSelectionChange, onViewTask,
}) => (
  <section className="phase-task-picker" aria-label={ariaLabel}>
    <div className="phase-task-picker__heading">
      <span>{heading}</span>
      <span className="phase-task-picker__totals" aria-live="polite">{totals}</span>
      {headingAction}
    </div>
    <TaskChecklistRows items={items} completionOnly={completionOnly}
      onSelectionChange={onSelectionChange} onViewTask={onViewTask} />
  </section>
);
